import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { TotalModeEnum } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import type { BackError } from '../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { resolvableServiceValueForm2ApiMapper } from '../../ResolvableService/mappers/resolvable-service-value.form2api.mapper';
import type { SubmitHandler } from '../../ResolvableServiceUser/types/submit-handler.type';

import type { ManualTotalFormValues } from '../types/values.type';
import type { SwitchTotalModeMutation } from '../types/props.type';

interface Params {
  readonly mutation: SwitchTotalModeMutation;
  readonly onSuccess: () => void;
  readonly queryKeysToInvalidate: readonly (readonly string[])[];
  readonly serviceUsagesGroupId: Id;
  readonly serviceUserId: Id;
}

export const useSwitchTotalModeSubmit = ({
  mutation,
  onSuccess,
  queryKeysToInvalidate,
  serviceUsagesGroupId,
  serviceUserId,
}: Readonly<Params>) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmitFactory = useCallback(
    (targetTotalMode: TotalModeEnum): SubmitHandler<ManualTotalFormValues> =>
      ({ values, memos, formikHelpers: { setSubmitting }, setFormError }) => {
        setFormError(null);

        const isSwitchingToManual = targetTotalMode === TotalModeEnum.Manual;

        mutation(
          {
            manualTotal: isSwitchingToManual
              ? resolvableServiceValueForm2ApiMapper(values.value)
              : null,
            serviceUsagesGroupId,
            serviceUserId,
            totalMode: targetTotalMode,
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError: (error: unknown) => {
              setFormError(userFriendlyErrors(error as BackError));
              setSubmitting(false);
            },
            onSuccess: () => {
              queryKeysToInvalidate.forEach((queryKey) => {
                void queryClient.invalidateQueries({ queryKey });
              });
              enqueueSnackbar(
                isSwitchingToManual
                  ? 'Le total est désormais calculé manuellement.'
                  : 'Le total est de nouveau calculé automatiquement.',
              );
              setSubmitting(false);
              onSuccess();
            },
          },
        );

        return undefined;
      },
    [
      enqueueSnackbar,
      mutation,
      onSuccess,
      queryClient,
      queryKeysToInvalidate,
      serviceUsagesGroupId,
      serviceUserId,
    ],
  );

  return { handleSubmitFactory };
};
